import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Heart } from "lucide-react";

const FamilySection = () => {
  const { t } = useTranslation();

  return (
    <section data-testid="family-section" className="bg-charcoal py-12 md:py-16 px-6 relative overflow-hidden">
      <div className="absolute inset-0 opacity-5 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIzMDAiIGhlaWdodD0iMzAwIj48ZmlsdGVyIGlkPSJhIiB4PSIwIiB5PSIwIj48ZmVUdXJidWxlbmNlIGJhc2VGcmVxdWVuY3k9Ii43NSIgc3RpdGNoVGlsZXM9InN0aXRjaCIgdHlwZT0iZnJhY3RhbE5vaXNlIi8+PGZlQ29sb3JNYXRyaXggdHlwZT0ic2F0dXJhdGUiIHZhbHVlcz0iMCIvPjwvZmlsdGVyPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbHRlcj0idXJsKCNhKSIvPjwvc3ZnPg==')]" />
      <motion.div
        className="relative z-10"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8 }}
      >
        <div className="max-w-4xl mx-auto flex items-start" dir="ltr">
          {/* Left - Groom's family */}
          <div className="flex-1 text-left pl-2 md:pl-8">
            <p className="font-serif text-white/40 text-[10px] md:text-xs italic leading-relaxed">
              André Levy et Alain Tordjmann
            </p>
            <div className="w-8 h-px bg-gold/30 my-2" />
            <p className="font-serif text-white/60 text-[10px] md:text-xs leading-relaxed">
              Benedicte Tordjmann
            </p>
            <p className="font-serif text-white/60 text-[10px] md:text-xs leading-relaxed">
              et Armand Chetrit
            </p>
          </div>

          {/* Separator */}
          <div className="w-px h-16 bg-gold/20 mx-4 md:mx-8 mt-1" />

          {/* Right - Bride's family */}
          <div className="flex-1 text-right pr-2 md:pr-8">
            <p className="font-serif text-white/40 text-[10px] md:text-xs italic leading-relaxed">
              Anne Marie Encausse
            </p>
            <p className="font-serif text-white/40 text-[10px] md:text-xs italic leading-relaxed">
              Pierrette Capdeville et Gérard Brisset
            </p>
            <div className="w-8 h-px bg-gold/30 my-2 ml-auto" />
            <p className="font-serif text-white/60 text-[10px] md:text-xs leading-relaxed">
              Corinne et David Brisset
            </p>
          </div>
        </div>

        {/* In memoriam */}
        <div className="max-w-4xl mx-auto text-center mt-6 md:mt-8">
          <div className="w-12 h-px bg-gold/20 mx-auto mb-2" />
          <p className="font-serif text-white/30 text-[9px] md:text-[11px] italic leading-relaxed">
            {t("memorial_family")}
          </p>
          <p className="font-serif text-gold/50 text-[10px] md:text-xs mt-1" dir="rtl">
            ברוך דיין האמת
          </p>
        </div>

        {/* Couple names + date */}
        <div className="max-w-md mx-auto text-center mt-12 md:mt-16 pb-4">
          <Heart className="w-5 h-5 text-gold mx-auto mb-4" strokeWidth={1} fill="#C5A059" />
          <p className="font-serif text-white/80 text-xl md:text-2xl tracking-tight mb-3">
            {t("footer_text")}
          </p>
          <p className="font-sans text-white/30 text-xs tracking-[0.3em] uppercase">
            {t("wedding_date")}
          </p>
          <div className="w-8 h-px bg-gold/30 mx-auto mt-6" />
          <p className="font-sans text-white/20 text-xs mt-6 tracking-wider">
            {t("with_love")}
          </p>

          {/* WhatsApp contacts */}
          <div className="flex items-center justify-center gap-12 mt-10">
            <a
              href="https://wa.me/9720533015518"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="whatsapp-netanael"
              className="flex flex-col items-center gap-2 group"
            >
              <div className="w-11 h-11 rounded-full bg-[#25D366]/20 flex items-center justify-center group-hover:bg-[#25D366]/30 transition-colors">
                <svg viewBox="0 0 24 24" className="w-5 h-5 fill-[#25D366]">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
              </div>
              <span className="font-sans text-white/50 text-xs tracking-wide">Netanael</span>
            </a>
            <a
              href="https://wa.me/9720533307526"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="whatsapp-agathe"
              className="flex flex-col items-center gap-2 group"
            >
              <div className="w-11 h-11 rounded-full bg-[#25D366]/20 flex items-center justify-center group-hover:bg-[#25D366]/30 transition-colors">
                <svg viewBox="0 0 24 24" className="w-5 h-5 fill-[#25D366]">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
              </div>
              <span className="font-sans text-white/50 text-xs tracking-wide">Agathe</span>
            </a>
          </div>
        </div>
      </motion.div>
    </section>
  );
};

export default FamilySection;
